import type { Prisma } from "@prisma/client";
import type {
  BuyableItemEvent,
  BuyableItemHandler,
  PartialBuyableItem,
} from "../buyable-item.types";

type PartialBadgeBuyableItem = {
  badgeType?: { id: string; name?: string } | null;
};

type BadgeBuyableItem = {
  badgeType: {
    id: string;
    name?: string;
    quantityLeft?: number | null;
    groupMinSize?: number | null;
    event?: BuyableItemEvent;
  };
};

type BadgeWithEvent = {
  badgeType: { event: BuyableItemEvent };
};

const GROUP_KEY_PREFIX = "badge";

export const BadgeBuyableItemHandler = {
  displayName: "Badge",
  isCumulative: true,
  accounting: {
    generalAccountCode: "706119",
    invoiceLabel: "Billetterie",
  },

  getCompleteItemName(item: PartialBuyableItem): string | null {
    return item.badgeType?.name ? `Badge: ${item.badgeType.name}` : null;
  },

  getEvent(item: BadgeWithEvent): BuyableItemEvent {
    return item.badgeType.event;
  },

  isValid(item: PartialBadgeBuyableItem): boolean {
    return !!item.badgeType?.id;
  },

  findEquivalent(item: PartialBadgeBuyableItem): Prisma.BuyableItemWhereInput {
    return { badgeType: { id: item.badgeType?.id } };
  },

  create({ badgeType }: BadgeBuyableItem): Omit<Prisma.BuyableItemCreateInput, "buyableType"> {
    return { badgeType: { connect: { id: badgeType.id } } };
  },

  parseGroupKeyAsWhere(groupKey: string): Prisma.BuyableItemWhereInput {
    if (!BadgeBuyableItemHandler.isGroupKey(groupKey)) {
      throw new Error(`Invalid group key for badge: ${groupKey}`);
    }
    const [, badgeTypeId] = groupKey.split("|");
    return { badgeType: { id: badgeTypeId } };
  },

  isGroupKey(groupKey: string): boolean {
    return groupKey.startsWith(`${GROUP_KEY_PREFIX}|`);
  },

  getGroupKey(item: PartialBadgeBuyableItem): string {
    const badgeTypeId = item.badgeType?.id;
    if (!badgeTypeId) {
      throw new Error("Cannot get group key for invalid badge item");
    }
    return `${GROUP_KEY_PREFIX}|${badgeTypeId}`;
  },

  getPricingQuery(item: PartialBuyableItem): Prisma.PricingFindFirstArgs {
    return { where: { badgeType: { id: item.badgeType?.id } } };
  },

  getEventSearchFilter(searchText: string): Prisma.BuyableItemWhereInput {
    return {
      badgeType: {
        event: { name: { contains: searchText, mode: "insensitive" } },
      },
    };
  },
} as const satisfies BuyableItemHandler;
